/**
 * Hook SSE pour le streaming du chat (EX-39).
 * Reçoit: token, sources, graph, done, error
 */
import { useCallback, useRef, useState } from "react";

const BASE_URL = process.env.NODE_ENV === "production"
  ? window.location.origin
  : (process.env.REACT_APP_API_URL || "http://localhost:8020");

export function useSSEStream() {
  const [tokens, setTokens] = useState("");
  const [sources, setSources] = useState([]);
  const [graphData, setGraphData] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [queryMode, setQueryMode] = useState("");
  const [fallbackUsed, setFallbackUsed] = useState(false);
  const eventSourceRef = useRef(null);

  const startStream = useCallback((query, conversationId) => {
    if (eventSourceRef.current) {
      eventSourceRef.current.close();
    }

    setTokens("");
    setSources([]);
    setGraphData(null);
    setError(null);
    setIsLoading(true);

    const token = localStorage.getItem("access_token");
    const params = new URLSearchParams({ query, conversation_id: conversationId || "" });
    const url = `${BASE_URL}/api/retrieval/stream/?${params}`;

    // Utiliser fetch pour les SSE avec auth header
    const abortController = new AbortController();

    fetch(url, {
      headers: { Authorization: `Bearer ${token}` },
      signal: abortController.signal,
    }).then((response) => {
      const reader = response.body.getReader();
      const decoder = new TextDecoder();
      let buffer = "";

      function read() {
        reader.read().then(({ done, value }) => {
          if (done) {
            setIsLoading(false);
            return;
          }
          // Bufferiser : une frame SSE peut être coupée entre deux lectures réseau.
          buffer += decoder.decode(value, { stream: true });
          const lines = buffer.split("\n");
          buffer = lines.pop() ?? "";

          for (const line of lines) {
            if (line.startsWith("data: ")) {
              try {
                const data = JSON.parse(line.slice(6));
                switch (data.type) {
                  case "token":
                    setTokens((prev) => prev + data.content);
                    break;
                  case "sources":
                    setSources(data.sources || []);
                    break;
                  case "graph":
                    setGraphData({ nodes: data.nodes, edges: data.edges });
                    break;
                  case "done":
                    setQueryMode(data.query_mode || "");
                    setFallbackUsed(data.fallback || false);
                    setIsLoading(false);
                    break;
                  case "error":
                    setError(data.message);
                    setIsLoading(false);
                    break;
                }
              } catch {}
            }
          }
          read();
        });
      }
      read();
    }).catch((err) => {
      if (err.name !== "AbortError") {
        setError(err.message);
        setIsLoading(false);
      }
    });

    eventSourceRef.current = { close: () => abortController.abort() };
  }, []);

  const stopStream = useCallback(() => {
    if (eventSourceRef.current) {
      eventSourceRef.current.close();
      setIsLoading(false);
    }
  }, []);

  return { tokens, sources, graphData, isLoading, error, queryMode, fallbackUsed, startStream, stopStream };
}
